import { useCallback, useEffect, useRef, useState } from 'react';

export type VoiceMode = 'hold' | 'tap';

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionResultEventLike {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function browserVoiceSupported() {
  return typeof window !== 'undefined' && !!getSpeechRecognitionCtor();
}

export function useBrowserVoice(options: {
  mode: VoiceMode;
  silenceMs: number;
  onUtterance: (text: string) => void;
}) {
  const { mode, silenceMs, onUtterance } = options;
  const [listening, setListening] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [level, setLevel] = useState(0);
  const [firstPartialMs, setFirstPartialMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const modeRef = useRef(mode);
  const silenceMsRef = useRef(silenceMs);
  const onUtteranceRef = useRef(onUtterance);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const listeningRef = useRef(false);
  const holdingRef = useRef(false);
  const startedAtRef = useRef(0);
  const lastResultAtRef = useRef(0);
  const liveTextRef = useRef('');
  const firstPartialSetRef = useRef(false);
  const silenceTimerRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRafRef = useRef(0);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  modeRef.current = mode;
  silenceMsRef.current = silenceMs;
  onUtteranceRef.current = onUtterance;

  const stopMeter = useCallback(() => {
    if (analyserRafRef.current) {
      cancelAnimationFrame(analyserRafRef.current);
      analyserRafRef.current = 0;
    }
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;
    setLevel(0);
  }, []);

  const startMeter = useCallback(async () => {
    stopMeter();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);

      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(1, rms * 4));
        analyserRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // Recognition can still work without a visible level meter.
    }
  }, [stopMeter]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current != null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const commitAndStop = useCallback(() => {
    const text = liveTextRef.current.trim();
    listeningRef.current = false;
    holdingRef.current = false;
    setListening(false);
    clearSilenceTimer();
    try {
      recRef.current?.stop();
    } catch {
      /* already stopped */
    }
    stopMeter();
    if (text) {
      onUtteranceRef.current(text);
    }
    setLiveText('');
    liveTextRef.current = '';
  }, [clearSilenceTimer, stopMeter]);

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (modeRef.current !== 'tap') return;
    silenceTimerRef.current = window.setTimeout(() => {
      if (listeningRef.current && !holdingRef.current) {
        commitAndStop();
      }
    }, silenceMsRef.current);
  }, [clearSilenceTimer, commitAndStop]);

  const ensureRecognition = useCallback(() => {
    if (recRef.current) return recRef.current;
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return null;
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-GB';

    rec.onresult = (event) => {
      lastResultAtRef.current = Date.now();
      if (!firstPartialSetRef.current) {
        firstPartialSetRef.current = true;
        setFirstPartialMs(Date.now() - startedAtRef.current);
      }
      let combined = '';
      for (let i = 0; i < event.results.length; i += 1) {
        combined += event.results[i][0].transcript;
      }
      const next = combined.trim();
      liveTextRef.current = next;
      setLiveText(next);
      if (modeRef.current === 'tap') {
        armSilenceTimer();
      }
    };

    rec.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      if (event.error === 'not-allowed') {
        setError('Microphone permission denied. Allow the mic in the browser, then try again.');
      } else {
        setError(`Voice error: ${event.error}`);
      }
    };

    rec.onend = () => {
      if (!listeningRef.current) return;
      // Chrome often ends after a pause. Keep going while holding, or until tap silence fires.
      if (holdingRef.current || (modeRef.current === 'tap' && Date.now() - lastResultAtRef.current < silenceMsRef.current)) {
        try {
          rec.start();
        } catch {
          /* ignore overlapping start */
        }
        return;
      }
      commitAndStop();
    };

    recRef.current = rec;
    return rec;
  }, [armSilenceTimer, commitAndStop]);

  const beginListening = useCallback(async () => {
    setError(null);
    const rec = ensureRecognition();
    if (!rec) {
      setError('This browser does not support speech recognition. Use Chrome or Edge, or type instead.');
      return false;
    }
    liveTextRef.current = '';
    setLiveText('');
    firstPartialSetRef.current = false;
    setFirstPartialMs(null);
    startedAtRef.current = Date.now();
    lastResultAtRef.current = Date.now();
    listeningRef.current = true;
    setListening(true);
    await startMeter();
    try {
      rec.start();
    } catch {
      try {
        rec.abort();
        rec.start();
      } catch {
        setError('Could not start the microphone. Click once on the page, then try again.');
        listeningRef.current = false;
        setListening(false);
        stopMeter();
        return false;
      }
    }
    if (modeRef.current === 'tap') {
      armSilenceTimer();
    }
    return true;
  }, [armSilenceTimer, ensureRecognition, startMeter, stopMeter]);

  const startTap = useCallback(() => {
    if (listeningRef.current) {
      commitAndStop();
      return;
    }
    holdingRef.current = false;
    void beginListening();
  }, [beginListening, commitAndStop]);

  const onHoldPointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      holdingRef.current = true;
      void beginListening();
    },
    [beginListening],
  );

  const onHoldPointerUp = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      if (!holdingRef.current && !listeningRef.current) return;
      commitAndStop();
    },
    [commitAndStop],
  );

  useEffect(() => () => {
    listeningRef.current = false;
    holdingRef.current = false;
    clearSilenceTimer();
    try {
      recRef.current?.abort();
    } catch {
      /* ignore */
    }
    stopMeter();
  }, [clearSilenceTimer, stopMeter]);

  const speak = useCallback((text: string) => {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'en-GB';
    utter.rate = 1.02;
    window.speechSynthesis.speak(utter);
  }, []);

  const cancelSpeak = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, []);

  return {
    supported: browserVoiceSupported(),
    listening,
    liveText,
    level,
    firstPartialMs,
    error,
    startTap,
    onHoldPointerDown,
    onHoldPointerUp,
    speak,
    cancelSpeak,
  };
}
